/** @format */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
    Box,
    Typography,
    Chip,
    MenuItem,
    TextField,
    Table,
    TableBody,
    TableHead,
    TableRow,
    CircularProgress,
    Pagination,
    Collapse,
    Alert,
    Snackbar,
} from "@mui/material";
import {
    SupportAgentOutlined,
    AddCircleOutline,
    ConfirmationNumberOutlined,
} from "@mui/icons-material";
import { AppDispatch, RootState } from "../../redux/store/store";
import { selectActiveBusinessId } from "../../redux/selectors/collectSelector";
import {
    fetchMySupportTickets,
    createSupportTicket,
    clearSupportError,
} from "../../redux/slices/collectSupportSlice";
import {
    ISupportTicket,
    SupportTicketCategory,
    SupportTicketPriority,
    SupportTicketStatus,
} from "../../utility/types/collectTypes";
import {
    CollectPageWrapper,
    CollectHeroBanner,
    CollectHeroInner,
    CollectBadge,
    CollectHeroTitle,
    CollectHeroSubtitle,
    CollectContentArea,
    CollectSectionCard,
    CollectSectionHeader,
    CollectSectionTitle,
    CollectSectionSubtitle,
    CollectPrimaryButton,
    CollectSecondaryButton,
    CollectTableContainer,
    CollectTableHeadCell,
    CollectTableBodyCell,
    CollectEmptyState,
    CollectEmptyTitle,
} from "./styles";
import {
    SUPPORT_CATEGORY_OPTIONS,
    SUPPORT_PRIORITY_OPTIONS,
    supportStatusChipSx,
    supportStatusLabel,
    formatDateTime,
} from "./supportShared";

const PAGE_SIZE = 20;
const SUBJECT_MAX = 200;
const DESCRIPTION_MAX = 10000;

const STATUS_FILTERS: { value: string; label: string }[] = [
    { value: "", label: "All statuses" },
    { value: "open", label: "Open" },
    { value: "in_progress", label: "In progress" },
    { value: "waiting_on_user", label: "Waiting on you" },
    { value: "resolved", label: "Resolved" },
    { value: "closed", label: "Closed" },
    { value: "reopened", label: "Reopened" },
];

const CollectSupport: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const businessId = useSelector(selectActiveBusinessId);
    const { myTickets, myPagination, isLoading, isSubmitting, error, submitError } =
        useSelector((s: RootState) => s.collectSupport);

    const [page, setPage] = useState(1);
    const [statusFilter, setStatusFilter] = useState("");
    const [formOpen, setFormOpen] = useState(false);

    // New-ticket form state.
    const [category, setCategory] = useState<SupportTicketCategory>("technical");
    const [priority, setPriority] = useState<SupportTicketPriority>("normal");
    const [subject, setSubject] = useState("");
    const [description, setDescription] = useState("");
    const [toast, setToast] = useState<string | null>(null);

    const load = useCallback(
        (targetPage: number, status: string) => {
            if (!businessId) return;
            dispatch(
                fetchMySupportTickets({
                    businessId,
                    status: status || undefined,
                    page: targetPage,
                    limit: PAGE_SIZE,
                }),
            );
        },
        [businessId, dispatch],
    );

    useEffect(() => {
        load(page, statusFilter);
    }, [load, page, statusFilter]);

    const canSubmit = useMemo(
        () =>
            !!businessId &&
            subject.trim().length > 0 &&
            description.trim().length > 0 &&
            !isSubmitting,
        [businessId, subject, description, isSubmitting],
    );

    const resetForm = () => {
        setCategory("technical");
        setPriority("normal");
        setSubject("");
        setDescription("");
    };

    const handleSubmit = async () => {
        if (!businessId || !canSubmit) return;
        dispatch(clearSupportError());
        const result = await dispatch(
            createSupportTicket({
                businessId,
                payload: {
                    category,
                    priority,
                    subject: subject.trim(),
                    description: description.trim(),
                    diagnostics: {
                        appPath: window.location.pathname,
                        userAgent: navigator.userAgent,
                    },
                },
            }),
        );
        if (createSupportTicket.fulfilled.match(result)) {
            resetForm();
            setFormOpen(false);
            setToast(
                `Ticket ${result.payload.ticketNumber} created. A confirmation email is on its way (it may be delayed).`,
            );
            setPage(1);
            load(1, statusFilter);
        }
    };

    return (
        <CollectPageWrapper>
            <CollectHeroBanner>
                <CollectHeroInner>
                    <CollectBadge
                        icon={<SupportAgentOutlined sx={{ fontSize: 16 }} />}
                        label="Help & Support"
                    />
                    <CollectHeroTitle>How can we help?</CollectHeroTitle>
                    <CollectHeroSubtitle>
                        Submit a support request and our team will get back to
                        you. Track every ticket and its status right here.
                    </CollectHeroSubtitle>
                    <Box sx={{ mt: 2 }}>
                        <CollectSecondaryButton
                            startIcon={<AddCircleOutline />}
                            onClick={() => setFormOpen((v) => !v)}
                        >
                            {formOpen ? "Close form" : "New ticket"}
                        </CollectSecondaryButton>
                    </Box>
                </CollectHeroInner>
            </CollectHeroBanner>

            <CollectContentArea>
                {/* ── New ticket form ── */}
                <Collapse in={formOpen} unmountOnExit>
                    <CollectSectionCard>
                        <CollectSectionHeader>
                            <Box>
                                <CollectSectionTitle>
                                    New support ticket
                                </CollectSectionTitle>
                                <CollectSectionSubtitle>
                                    Tell us what's happening — the more detail,
                                    the faster we can help.
                                </CollectSectionSubtitle>
                            </Box>
                        </CollectSectionHeader>

                        {submitError && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {submitError}
                            </Alert>
                        )}

                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                                gap: 2,
                                mb: 2,
                            }}
                        >
                            <TextField
                                select
                                label="Category"
                                value={category}
                                onChange={(e) =>
                                    setCategory(
                                        e.target.value as SupportTicketCategory,
                                    )
                                }
                                fullWidth
                                size="small"
                            >
                                {SUPPORT_CATEGORY_OPTIONS.map((opt) => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                select
                                label="Priority"
                                value={priority}
                                onChange={(e) =>
                                    setPriority(
                                        e.target.value as SupportTicketPriority,
                                    )
                                }
                                fullWidth
                                size="small"
                            >
                                {SUPPORT_PRIORITY_OPTIONS.map((opt) => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Box>

                        <TextField
                            label="Subject"
                            value={subject}
                            onChange={(e) =>
                                setSubject(e.target.value.slice(0, SUBJECT_MAX))
                            }
                            fullWidth
                            size="small"
                            sx={{ mb: 2 }}
                            helperText={`${subject.length}/${SUBJECT_MAX}`}
                        />
                        <TextField
                            label="Description"
                            value={description}
                            onChange={(e) =>
                                setDescription(
                                    e.target.value.slice(0, DESCRIPTION_MAX),
                                )
                            }
                            fullWidth
                            multiline
                            minRows={4}
                            size="small"
                            sx={{ mb: 2 }}
                            helperText={`${description.length}/${DESCRIPTION_MAX}`}
                        />

                        <Box sx={{ display: "flex", gap: 1.5 }}>
                            <CollectPrimaryButton
                                onClick={handleSubmit}
                                disabled={!canSubmit}
                            >
                                {isSubmitting ? (
                                    <CircularProgress size={20} color="inherit" />
                                ) : (
                                    "Submit ticket"
                                )}
                            </CollectPrimaryButton>
                        </Box>
                    </CollectSectionCard>
                </Collapse>

                {/* ── My tickets table ── */}
                <CollectSectionCard>
                    <CollectSectionHeader>
                        <Box>
                            <CollectSectionTitle>
                                My tickets
                            </CollectSectionTitle>
                            <CollectSectionSubtitle>
                                {myPagination.total} total
                            </CollectSectionSubtitle>
                        </Box>
                        <TextField
                            select
                            label="Status"
                            value={statusFilter}
                            onChange={(e) => {
                                setStatusFilter(e.target.value);
                                setPage(1);
                            }}
                            size="small"
                            sx={{ minWidth: 180 }}
                        >
                            {STATUS_FILTERS.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </CollectSectionHeader>

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {isLoading ? (
                        <Box sx={{ textAlign: "center", py: 6 }}>
                            <CircularProgress />
                        </Box>
                    ) : myTickets.length === 0 ? (
                        <CollectEmptyState>
                            <ConfirmationNumberOutlined
                                sx={{ fontSize: 40, color: "#c7ccd6", mb: 1 }}
                            />
                            <CollectEmptyTitle>No tickets yet</CollectEmptyTitle>
                            <Typography sx={{ fontSize: "0.85rem" }}>
                                When you submit a support request it will appear
                                here.
                            </Typography>
                        </CollectEmptyState>
                    ) : (
                        <>
                            <CollectTableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <CollectTableHeadCell>
                                                Ticket
                                            </CollectTableHeadCell>
                                            <CollectTableHeadCell>
                                                Subject
                                            </CollectTableHeadCell>
                                            <CollectTableHeadCell>
                                                Category
                                            </CollectTableHeadCell>
                                            <CollectTableHeadCell>
                                                Status
                                            </CollectTableHeadCell>
                                            <CollectTableHeadCell>
                                                Priority
                                            </CollectTableHeadCell>
                                            <CollectTableHeadCell>
                                                Created
                                            </CollectTableHeadCell>
                                            <CollectTableHeadCell>
                                                Last update
                                            </CollectTableHeadCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {myTickets.map((t: ISupportTicket) => (
                                            <TableRow
                                                key={t._id}
                                                hover
                                                sx={{ cursor: "pointer" }}
                                                onClick={() =>
                                                    navigate(
                                                        `/collect/support/${t._id}`,
                                                    )
                                                }
                                            >
                                                <CollectTableBodyCell>
                                                    <strong>
                                                        {t.ticketNumber}
                                                    </strong>
                                                </CollectTableBodyCell>
                                                <CollectTableBodyCell>
                                                    {t.subject}
                                                </CollectTableBodyCell>
                                                <CollectTableBodyCell>
                                                    {
                                                        SUPPORT_CATEGORY_OPTIONS.find(
                                                            (o) =>
                                                                o.value ===
                                                                t.category,
                                                        )?.label
                                                    }
                                                </CollectTableBodyCell>
                                                <CollectTableBodyCell>
                                                    <Chip
                                                        size="small"
                                                        label={supportStatusLabel(
                                                            t.status as SupportTicketStatus,
                                                        )}
                                                        sx={supportStatusChipSx(
                                                            t.status as SupportTicketStatus,
                                                        )}
                                                    />
                                                </CollectTableBodyCell>
                                                <CollectTableBodyCell>
                                                    {t.priority}
                                                </CollectTableBodyCell>
                                                <CollectTableBodyCell>
                                                    {formatDateTime(t.createdAt)}
                                                </CollectTableBodyCell>
                                                <CollectTableBodyCell>
                                                    {formatDateTime(
                                                        t.lastReplyAt ||
                                                        t.lastStatusChangeAt ||
                                                        t.updatedAt,
                                                    )}
                                                </CollectTableBodyCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </CollectTableContainer>

                            {myPagination.totalPages > 1 && (
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                        mt: 3,
                                    }}
                                >
                                    <Pagination
                                        count={myPagination.totalPages}
                                        page={page}
                                        onChange={(_e, p) => setPage(p)}
                                        color="primary"
                                    />
                                </Box>
                            )}
                        </>
                    )}
                </CollectSectionCard>
            </CollectContentArea>

            <Snackbar
                open={!!toast}
                autoHideDuration={6000}
                onClose={() => setToast(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    severity="success"
                    onClose={() => setToast(null)}
                    sx={{ width: "100%" }}
                >
                    {toast}
                </Alert>
            </Snackbar>
        </CollectPageWrapper>
    );
};

export default CollectSupport;
